import { Component, computed, input, output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { LucideChevronLeft, LucideChevronRight } from '@lucide/angular';
import {
  injectPaginationState,
  NgpPagination,
  NgpPaginationNext,
  NgpPaginationPrevious,
} from 'ng-primitives/pagination';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { DropdownComponent, DropdownOption } from '../../molecules/dropdown/dropdown.component';
import { InlineButtonComponent } from '../../atoms/inline-button/inline-button.component';
import { PaginationDotComponent } from './pagination-dot/pagination-dot.component';
import { PaginationNumberComponent } from './pagination-number/pagination-number.component';
import { TextDirective } from '../../typography/text.directive';

export type PaginationVariant = 'Dots' | 'Numbers';
type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

@Component({
  selector: 'joii-pagination',
  imports: [
    PaginationDotComponent,
    PaginationNumberComponent,
    InlineButtonComponent,
    DropdownComponent,
    TextDirective,
    NgpPaginationNext,
    NgpPaginationPrevious,
  ],
  hostDirectives: [
    {
      directive: NgpPagination,
      inputs: ['ngpPaginationPage:page', 'ngpPaginationPageCount:pageCount', 'ngpPaginationDisabled:disabled'],
      outputs: ['ngpPaginationPageChange:pageChange'],
    },
  ],
  providers: [provideValueAccessor(PaginationComponent)],
  host: {
    '[attr.data-variant]': 'variant()',
    '(focusout)': 'onTouched?.()',
  },
  template: `
    <button
      ngpPaginationPrevious
      data-page-chevron
      joii-inline-button
      variant="secondary"
      [transparent]="true"
      [borderless]="true"
      [prefixIcon]="chevronLeft"
      [size]="'sm'"
    ></button>
    <ul>
      @for (page of pages(); track page) {
        <li>
          @if (page === 'start-ellipsis' || page === 'end-ellipsis') {
            <span joii-text [size]="3" color="tint2" data-page-ellipsis aria-hidden="true">…</span>
          } @else {
            @switch (variant()) {
              @case ('Dots') {
                <button joii-pagination-dot [page]="page"></button>
              }
              @case ('Numbers') {
                <button joii-pagination-number [page]="page">{{ page }}</button>
              }
            }
          }
        </li>
      }
    </ul>
    <button
      ngpPaginationNext
      data-page-chevron
      joii-inline-button
      variant="secondary"
      [transparent]="true"
      [borderless]="true"
      [prefixIcon]="chevronRight"
      [size]="'sm'"
    ></button>
    @if (pageSizeDropdownOptions().length > 0) {
      <joii-dropdown
        data-page-size
        size="sm"
        [options]="pageSizeDropdownOptions()"
        [value]="selectedPageSizeOption()"
        [disabled]="state().disabled() || pageSizeDropdownOptions().length <= 1"
        (valueChange)="onPageSizeSelected($event)"
      />
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
    }
    :host [data-page-size] {
      margin-left: var(--Vetai-Spacing-4);
    }
    :host ul {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-2);
      flex-wrap: nowrap;
      margin: 0;
      padding: 0;
      list-style: none;
    }
  `,
})
export class PaginationComponent implements ControlValueAccessor {
  variant = input<PaginationVariant>('Numbers');
  /** Currently selected page size. Only used when `pageSizeOptions` is provided. */
  pageSize = input<number | undefined>(undefined);
  /** Page sizes to choose from. The page-size selector is only shown when this is non-empty. */
  pageSizeOptions = input<number[]>([]);
  /** Emitted when the user picks a different page size. The page is then reset to 1 (emitting `pageChange` if it changed). */
  readonly pageSizeChange = output<number>();

  protected readonly pageSizeDropdownOptions = computed<DropdownOption<number>[]>(() =>
    this.pageSizeOptions().map((size) => ({ name: String(size), value: size }))
  );
  protected readonly selectedPageSizeOption = computed(() =>
    this.pageSizeDropdownOptions().find((option) => option.value === Number(this.pageSize()))
  );
  protected readonly state = injectPaginationState();
  /** How many page numbers to show at once. The window follows the current page and is clamped at both ends. */
  visiblePageRange = input<number>(5);
  /**
   * How many pages to always show at each end, outside the window. Gaps are shown as an ellipsis.
   * Only applies to the `Numbers` variant.
   */
  boundaryCount = input<number>(0);
  protected readonly pages = computed<PaginationItem[]>(() => {
    // A page count that has not loaded yet can arrive as undefined or NaN.
    const rawPageCount = Number(this.state().pageCount());
    const pageCount = Number.isFinite(rawPageCount) ? Math.max(0, Math.floor(rawPageCount)) : 0;
    const range = Math.max(1, Math.floor(this.visiblePageRange()));
    if (pageCount <= range) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const current = Math.min(Math.max(1, this.state().page()), pageCount);
    const start = Math.min(Math.max(1, current - Math.floor(range / 2)), pageCount - range + 1);
    const end = start + range - 1;
    const window = Array.from({ length: range }, (_, i) => start + i);

    const boundary = this.variant() === 'Numbers' ? Math.max(0, Math.floor(this.boundaryCount())) : 0;
    if (boundary === 0) {
      return window;
    }

    const leading: PaginationItem[] = [];
    for (let page = 1; page <= Math.min(boundary, start - 1); page++) {
      leading.push(page);
    }
    if (start - 1 > boundary + 1) {
      leading.push('start-ellipsis');
    } else if (start - 1 === boundary + 1) {
      // A gap of exactly one page shows that page rather than an ellipsis.
      leading.push(boundary + 1);
    }

    const trailing: PaginationItem[] = [];
    const trailingStart = Math.max(end + 1, pageCount - boundary + 1);
    if (trailingStart - end - 1 > 1) {
      trailing.push('end-ellipsis');
    } else if (trailingStart - end - 1 === 1) {
      trailing.push(end + 1);
    }
    for (let page = trailingStart; page <= pageCount; page++) {
      trailing.push(page);
    }

    return [...leading, ...window, ...trailing];
  });
  private onChange?: ChangeFn<number>;
  protected onTouched?: TouchedFn;

  chevronLeft = LucideChevronLeft;
  chevronRight = LucideChevronRight;

  constructor() {
    this.state().pageChange.subscribe((value) => this.onChange?.(value));
  }

  protected onPageSizeSelected(option: DropdownOption<number> | undefined): void {
    if (!option || option.value === Number(this.pageSize())) return;
    this.pageSizeChange.emit(option.value);
    if (this.state().page() !== 1) {
      this.state().setPage(1);
    }
  }

  /** Write a new value to the control */
  writeValue(value: number | null | undefined): void {
    const page = value != null && Number.isFinite(Number(value)) ? Number(value) : 1;
    // writeValue is the model -> view direction, so it must not emit: the
    // emitted change would come straight back through onChange as a view -> model
    // update and loop forever.
    this.state().setPage(page, { emit: false });
  }
  /** Register a callback to be called when the value changes */
  registerOnChange(fn: ChangeFn<number>): void {
    this.onChange = fn;
  }
  /** Register a callback to be called when the control is touched */
  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  /** Disable the pagination when the form control is disabled */
  setDisabledState(isDisabled: boolean): void {
    this.state().setDisabled(isDisabled);
  }
}
